// AdminArticlesPage.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { articlesService } from '../Services/articlesService';
import { toast } from 'sonner';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Save,
  X,
  Upload,
  Loader2,
  FileText,
  Calendar,
  Tag,
} from 'lucide-react';
import QuillEditor from '../components/TipTapEditor';

const AdminArticlesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    thumbnail: '',
    category: 'sante-adulte',
    difficulty: 'Facile',
    budget: '',
    tags: '',
    images: [],
    published: false,
  });

  const [uploadingImage, setUploadingImage] = useState(false);

  const categories = [
    { value: 'petite-enfance', label: 'Petite Enfance (0-2 ans)' },
    { value: 'enfance', label: 'Enfance (3-5 ans)' },
    { value: 'preado', label: 'Préado (6-12 ans)' },
    { value: 'adolescence', label: 'Adolescence (13-18 ans)' },
    { value: 'adulte', label: '18 ans +' },
    { value: 'senior', label: 'Senior' },
  ];

  const difficulties = ['Facile', 'Moyen', 'Difficile'];

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      setLoading(true);
      const data = await articlesService.getAllArticles();
      setArticles(data);
    } catch (error) {
      toast.error('Erreur de chargement des articles');
    } finally {
      setLoading(false);
    }
  };

  const handleNewArticle = () => {
    setEditingArticle(null);
    setFormData({
      title: '',
      description: '',
      content: '',
      thumbnail: '',
      category: 'amenagement',
      difficulty: 'Facile',
      budget: '',
      tags: '',
      images: [],
      published: false,
    });
    setShowEditor(true);
  };

  const handleEditArticle = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title || '',
      description: article.description || '',
      thumbnail: article.thumbnail || '',
      content: article.content || '',
      category: article.category || 'amenagement',
      difficulty: article.difficulty || 'Facile',
      budget: article.budget || '',
      tags: article.tags?.join(', ') || '',
      images: article.images || [],
      published: article.published || false,
    });
    setShowEditor(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop volumineuse (max 5MB)');
      return;
    }

    try {
      setUploadingImage(true);
      const articleId = editingArticle?.id || 'temp-' + Date.now();
      const imageUrl = await articlesService.uploadImage(file, articleId);

      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, imageUrl],
      }));

      toast.success('Image uploadée');
    } catch (error) {
      toast.error('Erreur upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = (imageUrl) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((img) => img !== imageUrl),
    }));
  };

  const handleSave = async () => {
    if (!formData.title || !formData.description) {
      toast.error('Titre et description requis');
      return;
    }

    try {
      setSaving(true);

      const articleData = {
        title: formData.title,
        description: formData.description,
        content: formData.content,
        category: formData.category,
        thumbnail: formData.thumbnail,
        difficulty: formData.difficulty,
        budget: formData.budget,
        tags: formData.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        images: formData.images,
        published: formData.published,
      };

      if (editingArticle) {
        await articlesService.updateArticle(editingArticle.id, articleData);
        toast.success('Article mis à jour');
      } else {
        await articlesService.createArticle(articleData, user.uid);
        toast.success('Article créé');
      }

      setShowEditor(false);
      loadArticles();
    } catch (error) {
      console.error('❌ Erreur sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm('Supprimer cet article ?')) return;

    try {
      await articlesService.deleteArticle(articleId);
      toast.success('Article supprimé');
      loadArticles();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleTogglePublish = async (article) => {
    try {
      await articlesService.updateArticle(article.id, {
        published: !article.published,
      });
      toast.success(article.published ? 'Article dépublié' : 'Article publié');
      loadArticles();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (showEditor) {
    return (
      <AdminLayout>
        <div className="max-w-5xl mx-auto px-4 py-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {editingArticle ? "Modifier l'article" : 'Nouvel article'}
            </h1>
            <Button
              onClick={() => setShowEditor(false)}
              variant="outline"
              className="dark:border-gray-700 dark:text-gray-300"
            >
              <X className="w-4 h-4 mr-2" />
              Annuler
            </Button>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Formulaire */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 p-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Contenu</h2>

                {/* Titre */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Titre *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Ex: Sécuriser la salle de bain"
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:border-blue-500 text-gray-900 dark:text-gray-100"
                  />
                </div>

                {/* Description */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description courte *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Résumé en 1-2 phrases..."
                    rows="2"
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:border-blue-500 text-gray-900 dark:text-gray-100 resize-none"
                  />
                </div>

                {/* Contenu */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Contenu complet
                  </label>
                  <QuillEditor
                    value={formData.content}
                    onChange={(html) => setFormData({ ...formData, content: html })}
                  />
                </div>
              </Card>

              {/* Miniature */}
              <Card className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 p-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  Miniature
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Image affichée dans les cards articles (ratio 3:4 recommandé, ~300×400px)
                </p>

                {formData.thumbnail ? (
                  <div className="relative group">
                    <div className="w-44 h-56 rounded-xl overflow-hidden border-2 border-gray-200 dark:border-gray-700">
                      <img
                        src={formData.thumbnail}
                        alt="Miniature"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <button
                      onClick={() => setFormData((prev) => ({ ...prev, thumbnail: '' }))}
                      className="absolute top-2 left-2 bg-red-500 text-white p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="block">
                    <div className="w-44 h-56 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          if (file.size > 5 * 1024 * 1024) {
                            toast.error('Image trop volumineuse (max 5MB)');
                            return;
                          }
                          try {
                            setUploadingImage(true);
                            const articleId = editingArticle?.id || 'temp-' + Date.now();
                            const imageUrl = await articlesService.uploadImage(file, articleId);
                            setFormData((prev) => ({ ...prev, thumbnail: imageUrl }));
                            toast.success('Miniature uploadée');
                          } catch (error) {
                            toast.error('Erreur upload miniature');
                          } finally {
                            setUploadingImage(false);
                          }
                        }}
                        className="hidden"
                        disabled={uploadingImage}
                      />
                      {uploadingImage ? (
                        <Loader2 className="w-6 h-6 animate-spin text-blue-500 mb-2" />
                      ) : (
                        <Upload className="w-6 h-6 text-gray-400 mb-2" />
                      )}
                      <p className="text-xs text-gray-500 text-center px-2">300×400px</p>
                    </div>
                  </label>
                )}
              </Card>

              {/* Images */}
              <Card className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 p-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Images</h2>

                <div className="mb-4">
                  <label className="block">
                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-8 text-center cursor-pointer hover:border-blue-500 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploadingImage}
                      />
                      {uploadingImage ? (
                        <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-2" />
                      ) : (
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      )}
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Cliquez pour uploader une image
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Max 5MB - JPG, PNG, WEBP
                      </p>
                    </div>
                  </label>
                </div>

                {/* Liste des images */}
                {formData.images.length > 0 && (
                  <div className="grid grid-cols-2 gap-4">
                    {formData.images.map((img, idx) => (
                      <div key={idx} className="relative group">
                        <img
                          src={img}
                          alt={`Image ${idx + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => handleRemoveImage(img)}
                          className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Publier */}
              <Card className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 p-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
                  Publication
                </h3>

                <label className="flex items-center gap-3 mb-4 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.published}
                    onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <span className="text-gray-700 dark:text-gray-300">Publier immédiatement</span>
                </label>

                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white"
                >
                  {saving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Save className="w-5 h-5 mr-2" />
                      Enregistrer
                    </>
                  )}
                </Button>
              </Card>

              {/* Métadonnées */}
              <Card className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 p-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Détails</h3>

                {/* Catégorie */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Catégorie
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-gray-100"
                  >
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Difficulté */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Difficulté
                  </label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-gray-100"
                  >
                    {difficulties.map((diff) => (
                      <option key={diff} value={diff}>
                        {diff}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Budget */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Budget estimé
                  </label>
                  <input
                    type="text"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    placeholder="Ex: 150-500€"
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-gray-100"
                  />
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tags (séparés par des virgules)
                  </label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="0-2 ans, 3-5 ans, 6-12 ans, 13-18 ans, 18 ans +, Senior, ..."
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-gray-100"
                  />
                </div>
              </Card>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Gestion des Articles
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {articles.length} article(s) • {articles.filter((a) => a.published).length} publié(s)
            </p>
          </div>
          <Button
            onClick={handleNewArticle}
            className="bg-gradient-to-r from-blue-500 to-purple-600 text-white"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nouvel article
          </Button>
        </div>

        {/* Liste des articles */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : articles.length === 0 ? (
          <Card className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 p-12 text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Aucun article
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Commencez par créer votre premier article
            </p>
            <Button
              onClick={handleNewArticle}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white"
            >
              <Plus className="w-5 h-5 mr-2" />
              Créer un article
            </Button>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article) => (
              <Card
                key={article.id}
                className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 p-6 hover:shadow-lg transition-all"
              >
                {/* Image */}
                {article.images && article.images[0] && (
                  <img
                    src={article.images[0]}
                    alt={article.title}
                    className="w-full h-40 object-cover rounded-lg mb-4"
                  />
                )}

                {/* Status */}
                <div className="flex items-center gap-2 mb-3">
                  {article.published ? (
                    <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs font-medium flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      Publié
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded text-xs font-medium flex items-center gap-1">
                      <EyeOff className="w-3 h-3" />
                      Brouillon
                    </span>
                  )}
                  <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs">
                    {article.difficulty}
                  </span>
                </div>

                {/* Titre */}
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2 line-clamp-2">
                  {article.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                  {article.description}
                </p>

                {/* Meta */}
                <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-500 mb-4">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {article.createdAt?.seconds
                      ? new Date(article.createdAt.seconds * 1000).toLocaleDateString('fr-FR')
                      : 'N/A'}
                  </span>
                  {article.tags && article.tags.length > 0 && (
                    <span className="flex items-center gap-1">
                      <Tag className="w-3 h-3" />
                      {article.tags.length}
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleEditArticle(article)}
                    variant="outline"
                    size="sm"
                    className="flex-1 dark:border-gray-700 dark:text-gray-300"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Modifier
                  </Button>
                  <Button
                    onClick={() => handleTogglePublish(article)}
                    variant="outline"
                    size="sm"
                    className="dark:border-gray-700 dark:text-gray-300"
                  >
                    {article.published ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    onClick={() => handleDelete(article.id)}
                    variant="outline"
                    size="sm"
                    className="text-red-600 dark:text-red-400 dark:border-gray-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminArticlesPage;
